import { controlCharsObject, strings } from "@quickpoint.me/shared";
import {
	type ReactElement,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import styled, { ThemeProvider } from "styled-components";
import { FilterName, FilterSelector } from "../../state/filter";
import { PageIndex, PageSizeWithPaddingSelector } from "../../state/page";
import {
	ConstrainDimensionsSelector,
	IsPreviewGenerator,
} from "../../state/ui";
import { GridContext, PositionContext } from "./DisplayElements";

import { useRecoilValue } from "recoil";
/** @module displayComponents/DisplayElements */
// @ts-expect-error: No types for this package
import smartquotes from "smartquotes";
import type { ElementsProps } from "../../@types/viewerati";
import type { ElementProps } from "../../functionalComponents/RenderPage";
import { startingPoint } from "../../lib/cssHelpers";
import { AllLoaded } from "../../state/selectors";
import { IsInFlexBoxContext } from "./Cell";
import Grid from "./Grid";

const StyledGridElements = styled.div<
	Omit<ElementsProps, "$stickyHeaderHeight">
>`
  align-items: ${(props) => props.theme.align};
  display: grid;
  grid-template-columns : 1fr;
  grid-template-rows : auto;
  align-items: ${(props) => startingPoint(props.theme.cell.startingPoint, false, props.$isInLandscape)};
  justify-items: ${(props) => props.theme.cell.align};
  height: ${(props) => (props.$isInFlexBox ? "unset" : "100%")};
  justify-content: ${(props) => startingPoint(props.theme.startingPoint, props.$isOverflowing, props.$isInLandscape)}; 
  width: 100%;
  min-height: 0;
  min-width: 0;
  ${(props) => {
		if (props.$position === "top" && !props.$isInLandscape) {
			return `
        overflow-y: scroll;
        z-index:1;
        &::-webkit-scrollbar {
        display: none;
        }`;
		}
		return "";
	}}
`;

/**
 * Gets the state holding the elements that are to be displayed in the current page
 * and returns a JSX element containing them with CSS and theming applied.
 * @returns JSX
 */
function DisplayGrid({
	pageElementsToDisplay,
}: {
	pageElementsToDisplay: ReactElement<ElementProps>[];
}) {
	const filterName = useRecoilValue(FilterName);
	const filter = useRecoilValue(FilterSelector(filterName));
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const pageSize = useRecoilValue(PageSizeWithPaddingSelector);
	const rows = pageElementsToDisplay.filter(
		(el) => el.props.type === controlCharsObject["---"].controlChar,
	);
	const lastRow = pageElementsToDisplay.filter(
		(el) => el.props.type === controlCharsObject["-->"].controlChar,
	)[0];
	const pageIndex = useRecoilValue(PageIndex);
	const allLoaded = useRecoilValue(AllLoaded);
	const overflowRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const isInFlexBox = useContext(IsInFlexBoxContext);

	const position = useContext(PositionContext);

	const baseFontSize = pageSize.baseFontSize;

	// add smartquotes to each element when the displayed content changes
	useEffect(() => {
		const styledGridElements = document.getElementsByClassName(
			StyledGridElements.styledComponentId,
		);
		for (const el of Array.from(styledGridElements)) {
			smartquotes(el);
		}
	}, [pageElementsToDisplay]);

	const firstRow = rows[0];
	const firstRowIdx = pageElementsToDisplay.indexOf(firstRow);
	const lastRowIdx =
		pageElementsToDisplay.indexOf(lastRow) === -1
			? pageElementsToDisplay.length - 1
			: pageElementsToDisplay.indexOf(lastRow);

	const top = pageElementsToDisplay.slice(0, firstRowIdx);
	const tail = pageElementsToDisplay.slice(lastRowIdx + 1);
	const grid = pageElementsToDisplay.slice(firstRowIdx, lastRowIdx + 1);

	// check if content is overflowing on mobile when using middleOut layout
	useLayoutEffect(() => {
		if (overflowRef.current) {
			const displayGridScrollHeight = overflowRef.current.scrollHeight;
			const displayGridHeight = overflowRef.current.clientHeight;
			const overflowing = displayGridScrollHeight > displayGridHeight;
			setIsOverflowing(overflowing);
			// //console.log("displayGridScrollHeight: ", displayGridScrollHeight);
			// //console.log("displayGridHeight:", displayGridHeight);
			// //console.log(overflowing);
			// //console.log("--------------");
		}
	}, [pageSize, pageElementsToDisplay, pageIndex, allLoaded]);

	return (
		<ThemeProvider theme={filter}>
			<GridContext.Provider value={true}>
				<StyledGridElements
					$baseFontSize={baseFontSize}
					data-testid="DisplayGrid"
					className={strings.css.displayGrid}
					$isOverflowing={isOverflowing}
					$isPreviewer={isPreviewer}
					$constrainDimensions={constrainDimensions}
					$isInFlexBox={isInFlexBox}
					$isInLandscape={pageSize.landscape}
					$position={position}
					key={`grid-${pageIndex}`}
					ref={overflowRef}
				>
					{top}
					<Grid elements={grid} />
					{tail}
				</StyledGridElements>
			</GridContext.Provider>
		</ThemeProvider>
	);
}

export default DisplayGrid;
